import FileSaver from 'file-saver';
import React, { ReactElement, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Area,
  AreaChart,
  CartesianGrid,
  LabelProps,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CurveType } from 'recharts/types/shape/Curve';
import usePngFromRecharts from '../../hooks/usePngFromRecharts';
import { addComponentToPdfExport } from '../../redux/fileExport/actions';
import { PdfComponentType } from '../../types/redux/pdfExports/FileExportsStore';
import ChartDownloadButton from '../buttons/ChartDownloadButton';
import ExportButton from '../feedback/ExportButton';
import makeStyles from '@mui/styles/makeStyles';
import { Typography, useTheme } from '@mui/material';

const useStyles = makeStyles(() => ({
  center: {
    margin: '0 auto',
    fontSize: '0.88rem',
  },
}));

interface AreaDescriptor {
  type:
    | string
    | 'basis'
    | 'basisClosed'
    | 'basisOpen'
    | 'linear'
    | 'linearClosed'
    | 'natural'
    | 'monotoneX'
    | 'monotoneY'
    | 'monotone'
    | 'step'
    | 'stepBefore'
    | 'stepAfter'
    | Function;
  lineColor: string;
  fillColor: string;
  strokeWidth?: number;
  dataKey: string;
  dot?: Boolean | Object | ReactElement | Function;
}

interface IGenericAreaChartProps {
  data: any[];
  areas: AreaDescriptor[];
  width: number | string;
  height: number | string;
  title: string;
  // smallTitle?: boolean;
  // xInterval?: XAxisProps['interval'];
  margin: any;
  // yAxisDomain?: any;
  minTickGap?: number;
  tooltipFormatter?: any;
  yAxisTickFormatter?: any;
  // showLegend?: boolean;
  yAxisLabel?: any;
  // legendWrapper?: CSSProperties;
  // legendVAlign?: 'top' | 'bottom' | 'middle';
  loading?: boolean;
  export?: boolean;
  // yAxisTicks?: any[];
  // additionalYAxis?: AdaptedYAxis[];
  error: number | string | Error | null;
  // backgroundColor?: string; // in case you need to style the background
  xAxisLabel?: string | LabelProps;
  // yAxisDataKey?: string;
  // yAxisOrientation?: 'left' | 'right';
  // xAxisDataKey?: string;
  // showYTicks?: boolean;
  // showXTick?: boolean;
  // legendFormatter?: any;
  // showDot?: boolean;
  showTitle?: boolean;
  // strokeWidth?: number;
  download?: boolean;
  id?: string;
  pdfTitle?: string; // Optionally spoecify a title for when exporting to pdf
  referenceLineY?: number;
  referenceLineYLabel?: string;
  gradientFill?: boolean;
  fillOpacity?: number;
  // monotone?: boolean;
}

function GenericAreaChart({
  data,
  height,
  width,
  areas,
  title,
  margin,
  error,
  ...props
}: IGenericAreaChartProps): ReactElement {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { ref, handleDownload } = usePngFromRecharts();

  useEffect(() => {
    dispatch(
      addComponentToPdfExport({
        identifier: props.id || 'no_id',
        handler: handleDownload,
        type: PdfComponentType.AREA_CHART,
        title: props.pdfTitle ? props.pdfTitle : title,
      }),
    );
  }, [ref]);

  const saveImage = async () => {
    const imageData = await handleDownload();
    if (imageData) {
      FileSaver.saveAs(imageData.data, title + '.png');
    }
  };
  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <div id="chart-title" style={{ width: '100%', marginLeft: '0.62rem' }}>
          <Typography
            variant={'h2'}
            style={{ color: theme.palette.primary.main }}
          >
            {props.showTitle && title}
          </Typography>
        </div>
        <div id="export_area" style={{ display: 'flex', alignItems: 'center' }}>
          {props.export && (
            <ExportButton exportData={data} fields={Object.keys(data[0])} />
          )}
          {props.download && <ChartDownloadButton handler={saveImage} />}
        </div>
      </div>
      <ResponsiveContainer
        width={width}
        height={height}
        className={classes.center}
      >
        <AreaChart
          data={data}
          ref={ref}
          margin={margin}
        >
          <defs>
            {props.gradientFill &&
              areas.map((area: AreaDescriptor) => {
                return (
                  <linearGradient
                    id={`color${area.dataKey}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                    key={`color${area.dataKey}`}
                  >
                    <stop
                      offset="5%"
                      stopColor={area.fillColor}
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor={area.fillColor}
                      stopOpacity={0}
                    />
                  </linearGradient>
                );
              })}
          </defs>
          <XAxis
            minTickGap={props.minTickGap || 30}
            label={props.xAxisLabel}
            dataKey="name"
          />

          <YAxis tickFormatter={props.yAxisTickFormatter || null} />
          <CartesianGrid strokeDasharray="3 3" />
          <Tooltip formatter={props.tooltipFormatter || null} />

          {areas.map((area: AreaDescriptor) => {
            return (
              <Area
                type={area.type as CurveType}
                dataKey={area.dataKey}
                stroke={area.lineColor}
                fillOpacity={props.fillOpacity ? props.fillOpacity : 1}
                fill={
                  props.gradientFill
                    ? `url(#color${area.dataKey})`
                    : area.fillColor
                }
                key={area.dataKey + title}
                strokeWidth={area.strokeWidth || 1}
              />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
  {
  }
}

export default React.memo(GenericAreaChart);
